import { HTMLAttributes, useMemo, useState } from 'react'

import { Button, Heading, Text, theme } from '@hhgtech/hhg-components'
import innertext from 'innertext'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatRecentPost } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { PREGNANCY_SLUG } from 'interfaces/constants'
import {
  Article,
  Category,
  CategoryKeyword,
  RecentPost,
} from 'interfaces/types'

import { ExploreCategory } from '../exploreCategory'
import { KeywordContainer } from '../keywordContainer'
import { StyledExploreCategoryWrapper } from './index.styled'

type ExploreCategoryWrapperProps = {
  category: Category
  keywords?: Array<CategoryKeyword>
  posts: Array<Article>
} & HTMLAttributes<HTMLDivElement>
export const ExploreCategoryWrapper = ({
  category,
  keywords,
  posts,
  ...rest
}: ExploreCategoryWrapperProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const [shouldRenderArticleByTag, setShouldRenderArticleByTag] = useState(
    false,
  )

  const {
    locale,
    router: { asPath },
  } = useRouterExtend()
  const isPregnancy =
    decodeURI(asPath.replace(/\//gi, '')) === PREGNANCY_SLUG[locale]

  const [articleByTag, setArticleByTag] = useState<RecentPost>()

  const handleKeywordChange = async (
    tagId: number,
    subCategoryId: number | string,
  ) => {
    if (tagId === -1) {
      setShouldRenderArticleByTag(false)
      return
    }
    const res = await callApiNew(PATHS.ARTICLE.GET_ARTICLE_BY_TAG, {
      params: {
        categoryId: subCategoryId,
        tagId,
      },
    })
    setArticleByTag(formatRecentPost(res))
    setShouldRenderArticleByTag(true)
  }

  const revisedPosts = useMemo(() => {
    if (isPregnancy) {
      return shouldRenderArticleByTag
        ? articleByTag?.posts?.slice(0, 3) || posts?.slice(0, 3)
        : posts?.slice(0, 3)
    } else {
      return shouldRenderArticleByTag
        ? articleByTag?.posts.slice(0, 3) || posts
        : posts
    }
  }, [shouldRenderArticleByTag, isPregnancy, articleByTag?.posts, posts])

  if (!revisedPosts || revisedPosts.length === 0) return null

  return (
    <StyledExploreCategoryWrapper {...rest}>
      <div className="title">
        <Heading tag="h3">
          {f(
            { id: 'categoryPage.explore' },
            {
              CatName: innertext(category.categoryName),
            },
          )}
        </Heading>
        {!isMobile && isPregnancy && (
          <a
            href={category.permalink}
            className="see-all pointer-event-child-none"
            data-event-category="Category Tag"
            data-event-action="See All Click"
            data-event-label={`${getHelloSitesUrl(locale)}${
              category.permalink
            }`}
          >
            <Button color="ghost" size="sm">
              <Text size="s4" type="semiBold" color={theme.colors.primaryBase}>
                {f({ id: 'categoryPage.seeAll', defaultMessage: 'See all' })}
              </Text>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M9.80322 8.99999L6.93746 6.13424C6.62696 5.82374 6.62696 5.31974 6.93746 5.00924C7.24796 4.69874 7.75196 4.69874 8.06246 5.00924L11.523 8.46974C11.8162 8.76299 11.8162 9.23774 11.523 9.53024L8.06246 12.9907C7.75196 13.3012 7.24796 13.3012 6.93746 12.9907C6.62696 12.6802 6.62696 12.1762 6.93746 11.8657L9.80322 8.99999Z"
                  fill="#2D87F3"
                />
              </svg>
            </Button>
          </a>
        )}
      </div>
      {keywords && keywords.length > 0 && (
        <KeywordContainer
          keywords={keywords}
          onKeywordChange={(tagId) =>
            handleKeywordChange(tagId, category.termId)
          }
        />
      )}

      <ExploreCategory
        posts={revisedPosts}
        categoryInfo={{
          permalink: category.permalink,
          termId: String(category.termId),
        }}
      />
      {isMobile && isPregnancy && (
        <a
          href={category.permalink}
          className="pointer-event-child-none"
          data-event-category="Category Tag"
          data-event-action="See All Click"
          data-event-label={`${getHelloSitesUrl(locale)}${category.permalink}`}
        >
          <Button color="ghost" size="sm" isBlock>
            <Text size="s3" type="semiBold" color={theme.colors.primaryBase}>
              {f({ id: 'categoryPage.seeAll', defaultMessage: 'See all' })}
            </Text>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M9.80322 8.99999L6.93746 6.13424C6.62696 5.82374 6.62696 5.31974 6.93746 5.00924C7.24796 4.69874 7.75196 4.69874 8.06246 5.00924L11.523 8.46974C11.8162 8.76299 11.8162 9.23774 11.523 9.53024L8.06246 12.9907C7.75196 13.3012 7.24796 13.3012 6.93746 12.9907C6.62696 12.6802 6.62696 12.1762 6.93746 11.8657L9.80322 8.99999Z"
                fill="#2D87F3"
              />
            </svg>
          </Button>
        </a>
      )}
    </StyledExploreCategoryWrapper>
  )
}
